/**
 * Authentication Store
 *
 * Manages session state (tokens, user) and auth flow state (loading, errors).
 * Implements automatic token refresh before expiration (5 minutes buffer).
 *
 * The Auth0 access token is stored in `accessToken` and sent as an Authorization: Bearer header.
 */
import { create } from "zustand";
import { Platform } from "react-native";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";
import * as AuthSession from "expo-auth-session";
import { TokenResponse, TokenResponseConfig, RefreshTokenRequestConfig } from "expo-auth-session";
import { jwtDecode } from "jwt-decode";
import { authorization } from "@src/config/runtime";
import { getEnvironment } from "@src/config/environment-store";
import { identify, track } from "@visioni/a3s-analytics";
import { identifySignedInUser } from "@src/services/analytics/amplitude";
import { LoginCompleted, Logout } from "./analytics";
import type { User, Auth0TokenResponse, JWT, AuthState, AuthSessionTokenResponse } from "./types";
import { mergeUser } from "./user-profile-merge";
import { parseJWT } from "./jwt-user";
import { clearLastUsedTenant } from "@src/features/tenants";
import { useImpersonationStore } from "./impersonation-store";
import LegacyKitModule from "../../../modules/legacy-kit";

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Tracks the in-flight refresh so authenticatedFetch can await it rather than
 * sending a stale token. Set when refreshToken() starts, cleared on completion.
 */
let refreshPromise: Promise<boolean> | null = null;

/** Returns the in-flight refresh promise, or null if no refresh is active. */
export function getRefreshPromise(): Promise<boolean> | null {
  return refreshPromise;
}

const persistAuthUser = (user: User): void => {
  appStorage.set(AppStorageKeys.AUTH_USER, user);
};

function parseRolesFromAccessToken(accessToken: string): string[] {
  try {
    const payload = jwtDecode<{ "urn:visioni:roles"?: string[] }>(accessToken);
    return payload["urn:visioni:roles"] ?? [];
  } catch {
    return [];
  }
}

// Check for existing session synchronously on web to prevent flash of sign-in screen
function getInitialAuthState(): {
  isAuthenticated: boolean;
  accessToken: string | null;
  user: User | null;
} {
  if (Platform.OS === "web" && typeof window !== "undefined" && window.localStorage) {
    try {
      const key = `secure_${SecureStorageKeys.jwtToken}`;
      const stored = window.localStorage.getItem(key);
      if (stored) {
        const jwt = JSON.parse(stored) as JWT;
        if (jwt?.accessToken) {
          const user = parseJWT(jwt.accessToken);
          if (user) {
            user.roles = parseRolesFromAccessToken(jwt.accessToken);
            return { isAuthenticated: true, accessToken: jwt.accessToken, user };
          }
        }
      }
    } catch {
      // Ignore errors, will initialize properly later
    }
  }
  return { isAuthenticated: false, accessToken: null, user: null };
}

const initialAuthState = getInitialAuthState();

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: initialAuthState.user,
  accessToken: initialAuthState.accessToken,
  isAuthenticated: initialAuthState.isAuthenticated,
  isInitialized: initialAuthState.isAuthenticated, // If already authenticated, consider initialized
  isAuthenticating: false,
  authError: null,
  isRefreshing: false,

  setAuthenticating: (isAuthenticating: boolean) => {
    set({ isAuthenticating });
  },

  setAuthError: (authError: string | null) => {
    set({ authError, isAuthenticating: false });
  },

  handleTokenExchange: async (tokenResponse: AuthSessionTokenResponse) => {
    try {
      if (!tokenResponse.accessToken) {
        throw new Error("No access token received");
      }

      const auth0Response = tokenResponseToAuth0Response(tokenResponse);
      await get().setAuth(auth0Response);
    } catch (error) {
      console.error("[AuthStore] Token exchange error:", error);
      set({
        authError: error instanceof Error ? error.message : "Authentication failed",
        isAuthenticating: false,
      });
      throw error;
    }
  },

  initialize: async () => {
    try {
      const cachedUser = appStorage.get<User>(AppStorageKeys.AUTH_USER);

      // If already authenticated from sync check (web), just refresh token in background
      if (get().isAuthenticated) {
        set({ isInitialized: true });
        get().refreshToken();
        return;
      }

      // Auth0 JWT tokens flow (default)
      const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);

      if (jwt?.accessToken) {
        const parsed = parseJWT(jwt.accessToken);

        if (parsed) {
          const user = mergeUser(parsed, cachedUser) ?? parsed;
          user.roles = parseRolesFromAccessToken(jwt.accessToken);
          persistAuthUser(user);

          set({
            user,
            accessToken: jwt.accessToken,
            isAuthenticated: true,
            isInitialized: true,
          });

          // Refresh token in background - tenant fetching is now handled by TenantProvider
          get().refreshToken();
          return;
        }
      }

      // No authentication tokens found - user needs to login
      set({ isInitialized: true });
    } catch (error) {
      console.error("[AuthStore] Initialization error:", error);
      set({ isInitialized: true });
    }
  },

  setAuth: async (tokens: Auth0TokenResponse, providedUser?: User | null) => {
    try {
      const jwt = toJWT(tokens);
      await secureStorage.setJSON(SecureStorageKeys.jwtToken, jwt);

      let user = providedUser;
      if (!user && tokens.id_token) {
        user = parseJWT(tokens.id_token);
      }

      if (user) {
        user = mergeUser(user, appStorage.get<User>(AppStorageKeys.AUTH_USER)) ?? user;
        user.roles = parseRolesFromAccessToken(tokens.access_token);
        persistAuthUser(user);
      }

      // Set auth state but keep isAuthenticating true until tenant fetch completes
      set({
        user: user ?? null,
        accessToken: tokens.access_token,
        isAuthenticated: true,
        isInitialized: true,
        isAuthenticating: true, // Keep loading state active
        authError: null,
      });

      if (user) {
        identifySignedInUser(user);
        track(LoginCompleted);
      }

      // Tenant fetching is now handled by TenantProvider via React Query
      set({ isAuthenticating: false });
    } catch (error) {
      console.error("[AuthStore] Failed to set auth:", error);
      set({ isAuthenticating: false });
      throw error;
    }
  },

  clearAuth: async () => {
    try {
      // Revoke refresh token before clearing (need to get it first)
      const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);
      if (jwt?.refreshToken) {
        await revokeRefreshToken(jwt.refreshToken);
      }

      // Clear impersonation state so it doesn't leak to the next session.
      useImpersonationStore.getState().clearImpersonation();

      // Flip auth state first so tenant-dependent screens unmount before tenant teardown.
      set({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        authError: null,
        isRefreshing: false,
      });

      // Clear all secure storage (tokens, PIN, etc.)
      await secureStorage.clearAll();

      // Clear all app storage (user preferences, auth user, biometric preferences, etc.)
      appStorage.clear();

      // Clear any credentials the old native app left in its own storage
      try {
        const legacyModule = LegacyKitModule as unknown as Record<string, unknown>;
        if (legacyModule && typeof legacyModule.clearAllLegacyCredentials === "function") {
          legacyModule.clearAllLegacyCredentials();
        } else {
          console.warn("[AuthStore] clearAuth: LegacyKitModule not available, skipping native credential wipe");
        }
      } catch (error) {
        console.error("[AuthStore] Failed to clear legacy credentials:", error);
        // Don't throw - continue with logout even if legacy clearing fails
      }

      // Clear tenant preference
      await clearLastUsedTenant();

      // Clear React Query cache to prevent data leakage between users
      try {
        // Lazy require to avoid circular dependency with query-client
        const { clearQueryCache } = require("@src/features/query-client");
        await clearQueryCache();
      } catch (error) {
        console.error("[AuthStore] Failed to clear query cache:", error);
        // Don't throw - continue with logout even if cache clearing fails
      }

      // Tracked before the reset, so Logout is sent under the user who logged out
      // (the Amplitude destination reads the user when `track` is called).
      track(Logout);
      identify(null);
    } catch (error) {
      console.error("[AuthStore] Failed to clear auth:", error);
      throw error;
    }
  },

  /**
   * Returns Auth0 JWT access token if available.
   */
  getAccessToken: async () => {
    const { accessToken } = get();

    if (accessToken) {
      return accessToken;
    }

    // Try to load from secure storage if not in state
    const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);
    if (jwt?.accessToken) {
      set({ accessToken: jwt.accessToken });
      return jwt.accessToken;
    }

    return null;
  },

  refreshToken: async (options?: { force?: boolean }) => {
    // If a refresh is already in flight, coalesce onto it
    if (refreshPromise) {
      return refreshPromise;
    }

    const doRefresh = async (): Promise<boolean> => {
      try {
        const jwt = await secureStorage.getJSON<JWT>(SecureStorageKeys.jwtToken);

        if (!jwt?.refreshToken) {
          return false;
        }

        if (!options?.force) {
          const issuedAt = jwt.issuedAt || Date.now();
          const expiresAt = issuedAt + jwt.expiresIn * 1000;
          const shouldRefresh = Date.now() >= expiresAt - TOKEN_REFRESH_BUFFER_MS;

          if (!shouldRefresh) {
            return true;
          }
        }

        // Only signal isRefreshing once we know a network call is needed.
        // This avoids disrupting useAuthReady-gated queries on no-op checks.
        set({ isRefreshing: true });

        const env = await getEnvironment();
        const auth = authorization(env);

        const tokenConfig: TokenResponseConfig = {
          accessToken: jwt.accessToken,
          refreshToken: jwt.refreshToken,
          expiresIn: jwt.expiresIn,
          issuedAt: jwt.issuedAt || Date.now(),
          tokenType: "bearer",
          scope: jwt.scope,
        };

        let tokenResponse = new TokenResponse(tokenConfig);

        const refreshConfig: RefreshTokenRequestConfig = {
          clientId: auth.clientId,
          refreshToken: jwt.refreshToken,
        };

        const endpointConfig: Pick<AuthSession.DiscoveryDocument, "tokenEndpoint"> = {
          tokenEndpoint: auth.discovery.tokenEndpoint,
        };

        tokenResponse = await tokenResponse.refreshAsync(refreshConfig, endpointConfig);

        const newJwt: JWT = {
          accessToken: tokenResponse.accessToken,
          refreshToken: tokenResponse.refreshToken || jwt.refreshToken,
          expiresIn: tokenResponse.expiresIn || jwt.expiresIn,
          issuedAt: Date.now(),
          scope: tokenResponse.scope || jwt.scope,
        };

        await secureStorage.setJSON(SecureStorageKeys.jwtToken, newJwt);

        const currentUser = get().user;
        const user = mergeUser(parseJWT(tokenResponse.accessToken), currentUser) ?? currentUser;
        if (user) {
          user.roles = parseRolesFromAccessToken(tokenResponse.accessToken);
          persistAuthUser(user);
        }

        set({
          accessToken: tokenResponse.accessToken,
          user,
          isAuthenticated: true,
          isRefreshing: false,
        });

        return true;
      } catch (error) {
        console.error("[AuthStore] Failed to refresh token:", error);
        await get().clearAuth();
        return false;
      } finally {
        refreshPromise = null;
      }
    };

    refreshPromise = doRefresh();
    return refreshPromise;
  },
}));

function toJWT(tokens: Auth0TokenResponse): JWT {
  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresIn: tokens.expires_in,
    issuedAt: Date.now(),
    scope: tokens.scope || "openid profile email offline_access",
  };
}

function tokenResponseToAuth0Response(tokenResponse: AuthSessionTokenResponse): Auth0TokenResponse {
  return {
    access_token: tokenResponse.accessToken,
    refresh_token: tokenResponse.refreshToken || "",
    id_token: tokenResponse.idToken || "",
    token_type: tokenResponse.tokenType || "Bearer",
    expires_in: tokenResponse.expiresIn || 0,
    scope: tokenResponse.scope || "",
  };
}

async function revokeRefreshToken(refreshToken: string): Promise<void> {
  try {
    const env = await getEnvironment();
    const auth = authorization(env);

    const response = await fetch(`https://${auth.domain}/oauth/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: auth.clientId,
        token: refreshToken,
      }),
    });

    if (!response.ok) {
      console.warn("[AuthStore] Token revocation failed:", response.status);
    }
  } catch (error) {
    console.warn("[AuthStore] Token revocation error:", error);
  }
}

/**
 * Canonical selector: true when the session has a valid token ready for API use.
 * Queries should gate on this rather than checking isAuthenticated alone.
 */
export function useAuthReady(): boolean {
  return useAuthStore((s) => s.isAuthenticated && s.isInitialized && !s.isRefreshing);
}
